import { BrowserModule } from '@angular/platform-browser';
import { EnvironmentProviders, ErrorHandler, NgModule, Provider } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { TranslateCompiler, TranslateModule } from '@ngx-translate/core';

import { environment } from '@env/environment';
import { AuthModule } from '@app/auth';
import { HomeModule } from './home/home.module';
import { ShellModule } from './shell/shell.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from '@shared/shared.module';
import { ApiPrefixInterceptor } from '@shared/http/api-prefix.interceptor';
import { HttpErrorHandlerInterceptor } from '@shared/http/http-error-handler-interceptor.service';
import { RouteReusableStrategy } from '@shared/route-reusable-strategy';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PlIconRegistryModule } from '@app/icon-library/lib/pl-icon-registry.service';
import { CourseContentModule } from '@app/courseContent/courseContent.module';
import { NgxZendeskWebwidgetConfig, NgxZendeskWebwidgetModule } from 'ngx-zendesk-webwidget';
import Bugsnag from '@bugsnag/js';
import { MockHttpInterceptor } from '@src/app.mock-backend.service';
import { PotentialifeErrorHandler } from '@shared/services/error.service';
import { MyLifeChangesModule } from '@app/myLifeChanges/myLifeChanges.module';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { ProfileModule } from '@app/profile/profile.module';
import { FriendsSurveyModule } from '@app/friendsSurvey/friendsSurvey.module';
import { ViaSurveyBeforeProgramModule } from '@app/viaSurveyBeforeProgram/viaSurveyBeforeProgram.module';
import { MyWeeklyLifeActsModule } from '@app/myWeeklyLifeActs/myWeeklyLifeActs.module';

const additionalProviders: Array<Provider | EnvironmentProviders> = [];

declare global {
  interface Window {
    pdfMake: typeof import('pdfmake/build/pdfmake');
  }
}

if (environment.production) {
  Bugsnag.start({
    apiKey: environment.bugsnagApiKey,
    appVersion: environment.version || 'Unknown Version',
    releaseStage: environment.releaseStage,
  });
}

if (environment.features.mockBackEnd) {
  additionalProviders.push({
    provide: HTTP_INTERCEPTORS,
    useClass: MockHttpInterceptor,
    multi: true,
  });
}

export class ZendeskConfig extends NgxZendeskWebwidgetConfig {
  public override lazyLoad: boolean = true;
  public accountUrl: string = 'potentialifehelp.zendesk.com';
  public callback(zE: any): void {
    zE('webWidget', 'hide');
  }
}

@NgModule({
  imports: [
    HomeModule, // this has to be first for the '' path to work
    BrowserModule,
    BrowserAnimationsModule,

    FormsModule,
    HttpClientModule,
    RouterModule,
    TranslateModule.forRoot({
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
    SharedModule,
    ShellModule,
    NgxZendeskWebwidgetModule.forRoot(ZendeskConfig),

    AuthModule,
    CourseContentModule,
    FriendsSurveyModule,
    MyLifeChangesModule,
    MyWeeklyLifeActsModule,
    ProfileModule,
    ViaSurveyBeforeProgramModule,

    PlIconRegistryModule,
    AppRoutingModule, // must be imported as the last module as it contains the fallback route
  ],
  declarations: [AppComponent],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPrefixInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorHandlerInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy,
    },
    ...additionalProviders,
    {
      provide: ErrorHandler,
      useClass: PotentialifeErrorHandler,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
